import { create } from "zustand";
import { MessageBody } from "../../darvin-shared/schema/zod/agent/chat-response.schema";
import {
  GeneralAssistantMessage as GeneralAssistantMessageShared,
  GeneralAssistantMessageTopicEnum,
  MessageTypeEnum,
} from "../../darvin-shared/schema/zod/agent/message-types.schema";
import { ProjectError, useProjectStore } from "../core_stores/useProjectStore";
import {
  AssistantMessageAnimationStatus,
  ProjectChatAssistantMessage,
  ProjectChatElement,
  ProjectChatTemporaryUserMessage,
  ProjectChatUserMessage,
} from "./chat_messages/chatModels";
import { useChatMessagesStore } from "./chat_messages/useChatMessagesStore";

export enum ProjectStatus {
  CREATING_FIRST_VERSION = "creatingFirstVersion",
  PROCESSING = "processing",
  FIXING_COMPILATION_ERRORS = "fixingCompilationErrors",
  JOB_DONE = "jobDone",
  NOT_ENOUGH_TOKENS = "notEnoughTokens",
  SUBMITTING = "submitting",
}

interface ProjectStatusActions {
  initialize: () => void;
  dispose: () => void;
  notifyingSubmitting: () => void;
}

interface ProjectStatusState {
  status: ProjectStatus | null;

  actions: ProjectStatusActions;
}

const useProjectStatusStore = create<ProjectStatusState>()((set, get) => {
  let unsubscribeChatElements: (() => void) | null = null;
  let unsubscribeProjectLatestMsg: (() => void) | null = null;
  let unsubscribeProjectError: (() => void) | null = null;

  function isProjectTimeout() {
    return useProjectStore.getState().error === ProjectError.TIMEOUT;
  }

  function onChatElementsChanged(chatElements: ProjectChatElement[] | null) {
    let status = get().status;
    if (isProjectTimeout() || (chatElements && chatElements.length == 0)) {
      status = ProjectStatus.JOB_DONE;
    } else if (chatElements && chatElements.length > 0) {
      const lastElement = chatElements[chatElements.length - 1];
      if (isFixingCompilationErrors(lastElement)) {
        status = ProjectStatus.FIXING_COMPILATION_ERRORS;
      } else if (isNotEnoughTokens(lastElement)) {
        status = ProjectStatus.NOT_ENOUGH_TOKENS;
      } else if (
        isLatestMsgTerminal() &&
        areAllMessagesFullyDisplayed(chatElements)
      ) {
        status = ProjectStatus.JOB_DONE;
      } else {
        const amountOfUserMessages = chatElements.filter(
          (el) =>
            el instanceof ProjectChatUserMessage ||
            el instanceof ProjectChatTemporaryUserMessage
        ).length;
        if (amountOfUserMessages === 1) {
          status = ProjectStatus.CREATING_FIRST_VERSION;
        } else {
          const chatElementsReversed = [...(chatElements ?? [])].reverse();
          const projectConfigIndex = chatElementsReversed.findIndex(
            (el) =>
              el instanceof ProjectChatAssistantMessage &&
              el.type === "projectConfig"
          );
          const latestUserMessageIndex = chatElementsReversed.findIndex(
            (el) => el instanceof ProjectChatUserMessage
          );
          status =
            projectConfigIndex !== -1 &&
            projectConfigIndex < latestUserMessageIndex
              ? ProjectStatus.CREATING_FIRST_VERSION
              : ProjectStatus.PROCESSING;
        }
      }
    }
    set({
      status,
    });
  }

  function onProjectJobDone() {
    // Job done is done, no matter if all the messages are fully displayed or not
    set({
      status: ProjectStatus.JOB_DONE,
    });
  }

  function onProjectJobStarted() {
    if (get().status !== ProjectStatus.JOB_DONE) {
      return;
    }
    set({
      status: ProjectStatus.PROCESSING,
    });
  }

  function isFixingCompilationErrors(lastElement: ProjectChatElement): boolean {
    return (
      lastElement instanceof ProjectChatAssistantMessage &&
      lastElement.type === "general" &&
      (lastElement.value as GeneralAssistantMessageShared).topic ===
        GeneralAssistantMessageTopicEnum.enum.fixingCompilationErrors
    );
  }

  function isNotEnoughTokens(lastElement: ProjectChatElement): boolean {
    return (
      lastElement instanceof ProjectChatAssistantMessage &&
      lastElement.type === "error" &&
      "type" in lastElement.value &&
      lastElement.value.type === "notEnoughTokens"
    );
  }

  function isLatestMsgTerminal(): boolean {
    return isTerminalMessage(useProjectStore.getState().latestMsg);
  }

  function isTerminalMessage(message?: MessageBody | null): boolean {
    return (
      message?.type === MessageTypeEnum.enum.jobDone ||
      message?.type === MessageTypeEnum.enum.error
    );
  }

  function isInitialMessage(message?: MessageBody | null): boolean {
    return message?.type === MessageTypeEnum.enum.jobStarted;
  }

  function areAllMessagesFullyDisplayed(
    chatElements: ProjectChatElement[]
  ): boolean {
    if (chatElements.at(-1) instanceof ProjectChatTemporaryUserMessage) {
      return false;
    }
    return chatElements.every((el) => {
      if (el instanceof ProjectChatAssistantMessage) {
        return el.animationStatus === AssistantMessageAnimationStatus.DISPLAYED;
      }
      return true;
    });
  }

  return {
    status: null,

    actions: {
      initialize: () => {
        unsubscribeChatElements = useChatMessagesStore.subscribe(
          (state) => state.chatElements,
          onChatElementsChanged
        );
        unsubscribeProjectLatestMsg = useProjectStore.subscribe(
          (state) => state.latestMsg,
          (message) => {
            if (isProjectTimeout()) {
              return;
            }
            if (isTerminalMessage(message)) {
              onProjectJobDone();
            } else if (isInitialMessage(message)) {
              onProjectJobStarted();
            }
          }
        );
        unsubscribeProjectError = useProjectStore.subscribe(
          (state) => state.error,
          (projectError) => {
            if (projectError === ProjectError.TIMEOUT) {
              set({ status: ProjectStatus.JOB_DONE });
            }
          }
        );
      },

      dispose: () => {
        if (unsubscribeChatElements) {
          unsubscribeChatElements();
          unsubscribeChatElements = null;
        }
        if (unsubscribeProjectLatestMsg) {
          unsubscribeProjectLatestMsg();
          unsubscribeProjectLatestMsg = null;
        }
        if (unsubscribeProjectError) {
          unsubscribeProjectError();
          unsubscribeProjectError = null;
        }
        set({ status: null });
      },

      notifyingSubmitting: () => {
        if (get().status !== ProjectStatus.JOB_DONE) {
          return;
        }
        set({ status: ProjectStatus.SUBMITTING });
      },
    },
  };
});

export const useProjectStatus = () =>
  useProjectStatusStore((state) => state.status);

export const useIsProjectProcessing = () =>
  useProjectStatusStore((state) =>
    state.status === null
      ? false
      : [
          ProjectStatus.PROCESSING,
          ProjectStatus.FIXING_COMPILATION_ERRORS,
          ProjectStatus.CREATING_FIRST_VERSION,
          ProjectStatus.SUBMITTING,
        ].includes(state.status)
  );

export const useProjectStatusActions = () =>
  useProjectStatusStore((state) => state.actions);
